import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarProvider } from "@/components/ui/sidebar";
import AdminSidebar from "../Sidebar";
import Header from "../Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Search, GraduationCap, Download, Filter } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import "@/styles/fonts.css";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, query } from "firebase/firestore";
import { db } from "../../firebase/config";

interface Alumni {
  id: string;
  name: string;
  alumniYear: string;
  className: string;
  finalGrade: string;
  studentId: string;
}

const SchoolAlumni = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [alumniData, setAlumniData] = useState<Alumni[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
            const token = await user.getIdTokenResult();
            // Simple schoolID check - extend if complex affiliation logic needed
            const schoolID = (token.claims.schoolID as string) || 
                             (token.claims.affiliations as any[])?.find((a: any) => a.schoolID)?.schoolID;

            if (schoolID) {
                const alumniRef = collection(db, `schools/${schoolID}/alumni`);
                // Use orderBy if possible, or just client side sort
                const q = query(alumniRef); 
                const snapshot = await getDocs(q);
                
                const fetchedAlumni: Alumni[] = snapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name || 'Unknown',
                    alumniYear: doc.data().alumniYear || '',
                    className: doc.data().className || 'Unknown',
                    finalGrade: doc.data().finalGrade || '-',
                    studentId: doc.data().registrationNumber || doc.data().studentId || 'N/A'
                }));
                setAlumniData(fetchedAlumni);
            }
        } catch (error) {
            console.error("Error fetching alumni:", error);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const filteredAlumni = alumniData.filter((alumni) =>
    alumni.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    alumni.alumniYear.includes(searchTerm) ||
    alumni.className.toLowerCase().includes(searchTerm.toLowerCase()) ||
    alumni.studentId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-[#FFFFFF] dark:bg-[#020817] overflow-hidden" style={{ fontFamily: 'var(--font-plus-jakarta)' }}>
        <AdminSidebar onSectionChange={() => {}} className="bg-[#FFFFFF] dark:bg-[#020817] border-r border-[#E2E8F0] dark:border-[#1E293B]" />
        
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <Header className="bg-[#FFFFFF] dark:bg-[#020817] border-b border-[#E2E8F0] dark:border-[#1E293B] shrink-0" />
          
          <main className="flex-1 p-6 overflow-y-auto bg-gray-50/30 dark:bg-black/20">
            <div className="max-w-7xl mx-auto space-y-6">
              
              {/* Top Navigation & Header */}
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Button 
                        onClick={() => navigate(-1)} 
                        variant="outline" 
                        size="sm"
                        className="h-8 gap-2 bg-white dark:bg-[#020817] border-gray-200 dark:border-gray-800 text-muted-foreground hover:text-foreground hover:bg-gray-100 dark:hover:bg-gray-800 transition-all shadow-sm"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back
                    </Button>
                  </div>
                  <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-50">School Alumni</h1>
                  <p className="text-muted-foreground">
                    Archive of former students and their academic records.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="gap-2 bg-white dark:bg-gray-800 shadow-sm">
                        <Download className="h-4 w-4" /> Export List
                    </Button>
                </div>
              </div>

              {/* Stats Overview */}
              <div className="grid gap-4 md:grid-cols-3">
                 <Card className="bg-white dark:bg-gray-900 border-indigo-100 dark:border-indigo-900/50 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Alumni</CardTitle>
                        <GraduationCap className="h-4 w-4 text-indigo-600" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{alumniData.length}</div>
                        <p className="text-xs text-muted-foreground">Registered graduates</p>
                    </CardContent>
                 </Card>
                 <Card className="bg-white dark:bg-gray-900 border-emerald-100 dark:border-emerald-900/50 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Top Grade (A/A+)</CardTitle>
                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {alumniData.filter(a => a.finalGrade.startsWith('A')).length}
                        </div>
                        <p className="text-xs text-muted-foreground">High achievers</p>
                    </CardContent>
                 </Card>
                 <Card className="bg-white dark:bg-gray-900 border-blue-100 dark:border-blue-900/50 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Latest Cohort</CardTitle>
                        <Filter className="h-4 w-4 text-blue-500" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {alumniData.length > 0 ? [...new Set(alumniData.map(a => a.alumniYear))].sort().pop() : '-'}
                        </div>
                        <p className="text-xs text-muted-foreground">Academic Year</p>
                    </CardContent>
                 </Card>
              </div>

              {/* Main Content Card */}
              <Card className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0f172a] shadow-md">
                <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <CardTitle className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100 flex items-center gap-2">
                            Alumni Directory
                        </CardTitle>
                        <CardDescription className="text-gray-500 dark:text-gray-400 mt-1">
                            Search and manage the complete history of graduated students.
                        </CardDescription>
                    </div>
                    <div className="relative w-full md:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                        placeholder="Search by name, ID, or year..."
                        className="pl-9 h-10 bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-800 focus-visible:ring-indigo-500"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-hidden">
                    <Table>
                      <TableHeader className="bg-gray-50/50 dark:bg-gray-900/40">
                        <TableRow className="border-b border-gray-200 dark:border-gray-800 hover:bg-transparent">
                          <TableHead className="w-[150px] font-semibold text-gray-600 dark:text-gray-400 pl-6 h-12">Registration ID</TableHead>
                          <TableHead className="font-semibold text-gray-600 dark:text-gray-400 h-12">Student Name</TableHead>
                          <TableHead className="font-semibold text-gray-600 dark:text-gray-400 h-12">Graduated Year</TableHead>
                          <TableHead className="font-semibold text-gray-600 dark:text-gray-400 h-12">Class</TableHead>
                          <TableHead className="font-semibold text-center text-gray-600 dark:text-gray-400 h-12">Final Grade</TableHead>
                          <TableHead className="text-right font-semibold text-gray-600 dark:text-gray-400 pr-6 h-12">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loading ? (
                             <TableRow>
                                <TableCell colSpan={6} className="h-48 text-center text-muted-foreground">
                                    Loading alumni records...
                                </TableCell>
                             </TableRow>
                        ) : filteredAlumni.length > 0 ? (
                          filteredAlumni.map((alumni) => (
                            <TableRow key={alumni.id} className="group hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0">
                              <TableCell className="font-mono text-xs text-muted-foreground pl-6 py-4">{alumni.studentId}</TableCell>
                              <TableCell className="py-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-900/40 dark:to-violet-900/40 flex items-center justify-center text-indigo-700 dark:text-indigo-300 font-bold text-sm ring-2 ring-white dark:ring-gray-950 shadow-sm">
                                        {alumni.name.charAt(0)}
                                    </div>
                                    <span className="font-medium text-gray-900 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                                        {alumni.name}
                                    </span>
                                </div>
                              </TableCell>
                              <TableCell className="py-4">
                                <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-transparent font-medium">
                                    {alumni.alumniYear}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-gray-600 dark:text-gray-400 text-sm py-4">{alumni.className}</TableCell>
                              <TableCell className="text-center py-4">
                                  <span className={`inline-flex items-center justify-center min-w-[36px] h-7 rounded-md text-xs font-bold border shadow-sm
                                      ${alumni.finalGrade.startsWith('A') ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-900/30' : 
                                        alumni.finalGrade.startsWith('B') ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-900/30' :
                                        'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900/30'}`}>
                                      {alumni.finalGrade}
                                  </span>
                              </TableCell>
                              <TableCell className="text-right pr-6 py-4">
                                <Button variant="secondary" size="sm" className="h-8 text-xs font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 border border-transparent dark:border-gray-700/50">
                                    View Profile
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} className="h-48 text-center">
                              <div className="flex flex-col items-center justify-center text-muted-foreground space-y-3">
                                <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                    <Search className="h-6 w-6 text-gray-400" />
                                </div>
                                <div className="space-y-1">
                                    <p className="font-medium text-gray-900 dark:text-gray-200">No alumni found</p>
                                    <p className="text-sm">Try adjusting your search terms.</p>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SchoolAlumni;
